// root 私有数据管理

import { rootStore } from '@yimoka/store';

const dataKey = {
  language: 'language',
  loadingLang: 'loadingLang',
  platform: 'platform',
  tenantID: 'tenantID',
  clientID: 'clientID',
  app: 'app',
};

/** 平台类型：h5 移动端 / pc 桌面端 */
export type Platform = 'h5' | 'pc';

export const PLATFORM_H5 = 'h5';
export const PLATFORM_PC = 'pc';

/** H5 判定断点宽度（px），小于此宽度视为移动端 */
export const PLATFORM_BREAKPOINT = 768;

/** 根据 UA 和窗口宽度检测是否为 H5 环境 */
export const detectIsH5 = (): boolean => {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent.toLowerCase();
  const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua);
  const isNarrowScreen = window.innerWidth < PLATFORM_BREAKPOINT;
  return isMobileUA || isNarrowScreen;
};

export const setPlatform = (platform: Platform) => {
  localStorage.setItem(dataKey.platform, platform);
  rootStore.setDataItem(dataKey.platform, platform);
};

export const getPlatform = (): Platform => {
  const platform = rootStore.getDataItem(dataKey.platform) as Platform | undefined;
  if (platform === PLATFORM_H5 || platform === PLATFORM_PC) return platform;
  const localPlatform = localStorage.getItem(dataKey.platform) as Platform | null;
  if (localPlatform === PLATFORM_H5 || localPlatform === PLATFORM_PC) {
    setPlatform(localPlatform);
    return localPlatform;
  }
  return detectIsH5() ? PLATFORM_H5 : PLATFORM_PC;
};

export const usePlatform = () => getPlatform();

export const setLanguage = (language: string) => {
  localStorage.setItem(dataKey.language, language);
  rootStore.setDataItem(dataKey.language, language);
  languageListeners.forEach(listener => listener());
};

export const getI18nLang = (lang: string) => lang ?? DEFAULT_LANGUAGE;

// 支持的语言
export const SUPPORT_LANGUAGES = [{ label: '中文', value: 'zh-CN', path: 'zh' }, { label: 'English', value: 'en-US', path: 'en' }];
// 默认的语文
export const DEFAULT_LANGUAGE = 'zh-CN';
const languageListeners = new Set<() => void>();

export const getLanguage = (): string => {
  const language = rootStore.getDataItem(dataKey.language);
  if (language) {
    return language;
  }
  const lang = localStorage.getItem(dataKey.language);
  if (lang) {
    setLanguage(lang);
    return lang;
  }
  const sysLang = window.navigator.language;
  const matchLang = SUPPORT_LANGUAGES?.find(item => (item?.value === sysLang || getI18nLang(item.value) === getI18nLang(sysLang)));
  if (matchLang) {
    return matchLang.value;
  }
  return DEFAULT_LANGUAGE;
};

export const useLanguage = () => getLanguage();

export const subscribeLanguage = (listener: () => void): (() => void) => {
  languageListeners.add(listener);
  return () => languageListeners.delete(listener);
};

export const setLoadingLang = (loading: boolean) => {
  rootStore.setDataItem(dataKey.loadingLang, loading);
};

export const getLoadingLang = () => rootStore.getDataItem(dataKey.loadingLang) ?? false;

export const useLoadingLang = () => getLoadingLang();

export const setClientID = (clientID: string) => {
  localStorage.setItem(dataKey.clientID, clientID);
  rootStore.setDataItem(dataKey.clientID, clientID);
};

export const getClientID = () => {
  const clientID = rootStore.getDataItem(dataKey.clientID);
  if (clientID) {
    return clientID;
  }
  const id = localStorage.getItem(dataKey.clientID);
  if (id) {
    setClientID(id);
    return id;
  }
  return '';
};

export const useClientID = () => getClientID();

export const setApp = (app: string) => {
  localStorage.setItem(dataKey.app, app);
  rootStore.setDataItem(dataKey.app, app);
};

export const getApp = () => {
  const app = rootStore.getDataItem(dataKey.app);
  if (app) {
    return app;
  }
  const localApp = localStorage.getItem(dataKey.app);
  if (localApp) {
    setApp(localApp);
    return localApp;
  }
  return '';
};

export const useApp = () => getApp();

export const getHostName = () => {
  const { hostname } = window.location;
  if (import.meta.env.DEV) {
    return hostname.replace('.local.', '.dev.');
  }
  return hostname;
};
